const MATCH_FEE_VARIANT_LOW  = 50242444820717; // qty ≤ 10
const MATCH_FEE_VARIANT_HIGH = 50242444853485; // qty ≥ 11

class Cart extends HTMLElement {
  constructor() {
    super();
    this.cart = {};
    this.fullCart = JSON.parse(document.getElementById('cart-obj')?.textContent);
    this.checkoutBtns = document.getElementById('cart__checkout');
    this.termsCheckbox = document.getElementById('terms');
    this.mainCart = document.querySelector('[data-section-type="cart"]');
    this.cartQtyEl = document.querySelector('.my-cart-link-container .js-cart-count');
    this._updating = false;
  }

  connectedCallback() {
    this.addListeners();
  }

  addListeners() {
    this.addEventListener('click', (event) => {
      const removeBtn = event.target.closest('[data-remove]');
      if (removeBtn) {
        event.preventDefault();
        this.lineUpdates(0, removeBtn.dataset.itemLine);
        return;
      }

      const updateBtn = event.target.closest('[data-update]');
      if (updateBtn) {
        event.preventDefault();
        const itemId = updateBtn.dataset.itemId;
        const line = updateBtn.dataset.itemLine;
        const qty = this.querySelector('.updates_' + itemId).value;
        this.lineUpdates(qty, line);
        return;
      }

      const checkoutBtn = event.target.closest('[data-checkout]');
      if (checkoutBtn && !this.termsCheckbox.checked) {
        event.preventDefault();
        alert(this.termsCheckbox.dataset.error);
      }
    });
  }

  // Fetches the live cart from the API and syncs it into this.cart/this.fullCart.
  // Called before every lineUpdates so line numbers are always accurate, even
  // when items were added externally (e.g. product page → redirect, or drawer)
  // after the cart page last rendered.
  async syncCart() {
    try {
      const response = await fetch('/cart.js', { credentials: 'same-origin' });
      const data = await response.json();
      // Preserve any existing section HTML but refresh the items array and count.
      this.cart = { ...this.cart, items: data.items, item_count: data.item_count };
      this.fullCart = data;
    } catch (error) {
      console.warn('Cart sync failed, proceeding with cached state:', error);
    }
  }

  async updateQty(line, quantity) {
    const data = { line: Number(line), quantity: Number(quantity), sections: ['main-cart'] };
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      return await response.json();
    } catch (error) {
      console.error('Error:', error);
      return null;
    }
  }

  async clearMatchProperty(line) {
    try {
      const cartRef = Object.keys(this.cart).length ? this.cart : this.fullCart;
      const item = cartRef.items[line - 1];
      if (!item) return;
      const { match: _removed, ...rest } = item.properties || {};
      // Explicitly set match to '' rather than omitting it — Shopify merges
      // properties on change.js so omitting a key leaves the old value in place.
      const payload = { line, quantity: item.quantity, properties: { ...rest, match: '' } };
      await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.error('Error clearing match property:', error);
    }
  }

  async swapMatchFeeVariant(feeLine, newQty, properties) {
    const targetVariant = Number(newQty) <= 10 ? MATCH_FEE_VARIANT_LOW : MATCH_FEE_VARIANT_HIGH;
    await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: feeLine, quantity: 0 }),
    });
    await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: targetVariant, quantity: Number(newQty), properties }),
    });
  }

  findParentItemLine(feeLine) {
    const cartRef = Object.keys(this.cart).length ? this.cart : this.fullCart;
    const feeItem = cartRef.items[feeLine - 1];
    if (!feeItem) return null;
    const feeSku = feeItem.properties?.sku;

    for (let i = 0; i < cartRef.items.length; i++) {
      const item = cartRef.items[i];
      if (item.handle === 'match-fee') continue;
      if (item.properties?.match !== 'true') continue;
      // Match by SKU if available, otherwise expect the fee to be directly after the item.
      if (feeSku ? item.sku === feeSku : i === feeLine - 2) return i + 1;
    }
    return null;
  }


  async lineUpdates(qty, line) {
    if (this._updating) return;
    this._updating = true;

    try {
      // Always sync before any operation so line numbers reflect the actual
      // current cart, not a potentially stale page-load or post-add snapshot.
      await this.syncCart();

      const cartRef = Object.keys(this.cart).length ? this.cart : this.fullCart;
      const targetItem = cartRef.items[line - 1];
      const isMatchFee = targetItem?.handle === 'match-fee';

      if (isMatchFee && qty < 2) {
        // Capture the fee's key before any async work so we can re-locate it
        // after the sync — a prior variant swap may have moved the fee to a
        // different cart position than the data-item-line attribute reflects.
        const feeKey = targetItem.key;
        const parentLine = this.findParentItemLine(line);
        if (parentLine !== null) await this.clearMatchProperty(parentLine);

        await this.syncCart();
        const feeLine = this.cart.items.findIndex(i => i.key === feeKey) + 1;
        if (feeLine > 0) {
          const data = await this.updateQty(feeLine, qty);
          if (data) this.cart = data;
        }
      } else {
        const matchesLine = this.findAssociatedMatchesLine(line);

        if (matchesLine !== null) {
          if (qty < 1) {
            // Remove fee first, then item.
            const feeData = await this.updateQty(matchesLine, qty);
            if (feeData) this.cart = feeData;

            // Fee removal shifts the item's line down by one if the fee was above it.
            const itemLine = matchesLine < line ? line - 1 : line;
            const itemData = await this.updateQty(itemLine, qty);
            if (itemData) this.cart = itemData;
          } else if (Number(qty) === 1) {
            // qty=1: no match fee needed — remove fee, clear match property, keep item at qty=1.
            const feeData = await this.updateQty(matchesLine, 0);
            if (feeData) this.cart = feeData;

            const newItemLine = Number(matchesLine < line ? line - 1 : line);
            const cartRef2 = Object.keys(this.cart).length ? this.cart : this.fullCart;
            const item = cartRef2.items[newItemLine - 1];
            if (item) {
              const { match: _removed, ...rest } = item.properties || {};
              const response = await fetch('/cart/change.js', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  line: newItemLine,
                  quantity: 1,
                  properties: { ...rest, match: '' },
                  sections: ['main-cart'],
                }),
              });
              const itemData = await response.json();
              if (itemData) this.cart = itemData;
            }
          } else {
            // Qty update: swap the match-fee variant if the threshold is crossed.
            const feeItem = cartRef.items[matchesLine - 1];
            const targetVariant = Number(qty) <= 10 ? MATCH_FEE_VARIANT_LOW : MATCH_FEE_VARIANT_HIGH;

            if (feeItem.variant_id !== targetVariant) {
              // Capture item key before swap so we can re-locate it after line numbers shift.
              const itemKey = cartRef.items[line - 1]?.key;
              await this.swapMatchFeeVariant(matchesLine, qty, feeItem.properties);
              await this.syncCart();
              const newLine = this.cart.items.findIndex(i => i.key === itemKey) + 1;
              if (newLine > 0) {
                // Update item qty without requesting sections — we reload below so a
                // stale section render can't leave the fee's remove button with a wrong
                // data-item-line that would cause the next removal to delete the item.
                await fetch('/cart/change.js', {
                  method: 'POST',
                  credentials: 'same-origin',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ line: Number(newLine), quantity: Number(qty) }),
                });
              }
              window.location.reload();
              return;
            } else {
              // Variant already correct — just update both qtys.
              const feeData = await this.updateQty(matchesLine, qty);
              if (feeData) this.cart = feeData;
              const itemData = await this.updateQty(line, qty);
              if (itemData) this.cart = itemData;
            }
          }
        } else {
          const data = await this.updateQty(line, qty);
          if (data) this.cart = data;
        }
      }

      this.renderCartItems();
    } finally {
      this._updating = false;
    }
  }

  findAssociatedMatchesLine(inputLine) {
    const cartRef = Object.keys(this.cart).length ? this.cart : this.fullCart;
    const itemRef = cartRef.items[inputLine - 1];

    if (!itemRef || !itemRef.properties) return null;
    if (itemRef.properties.match !== 'true') return null;
    if (itemRef.handle === 'match-fee') return null;

    // The fee is always appended to the cart immediately after its item, so
    // the fee at index `inputLine` (0-based) — i.e. the very next slot — is
    // strongly preferred. This correctly handles carts where multiple items
    // share the same SKU (or have no SKU), because a plain SKU-only search
    // would always return the *first* fee in the array rather than the one
    // that belongs to this item.
    let firstSkuMatch = null;

    for (let i = 0; i < cartRef.items.length; i++) {
      const item = cartRef.items[i];
      if (item.handle !== 'match-fee') continue;

      const skuMatches = itemRef.sku
        ? item.properties?.sku === itemRef.sku
        : i === inputLine; // No SKU: only accept the slot directly after the item

      if (!skuMatches) continue;

      // Best case: this fee sits directly after the item in the cart.
      if (i === inputLine) return i + 1;

      // Otherwise, keep the first SKU match as a fallback.
      if (firstSkuMatch === null) firstSkuMatch = i + 1;
    }

    return firstSkuMatch;
  }

  renderCartItems() {
    if (this.cart?.sections?.['main-cart']) {
      this.cartQtyEl.innerHTML = this.cart?.item_count;
      // Extract just the inner HTML of the <custom-cart> from the sections response
      // so we never create a nested custom element (which would add duplicate listeners).
      const tmp = document.createElement('div');
      tmp.innerHTML = this.cart.sections['main-cart'];
      const inner = tmp.querySelector('custom-cart');
      this.innerHTML = inner ? inner.innerHTML : this.cart.sections['main-cart'];

      // Re-parse the cart-obj that the freshly rendered section contains so
      // this.fullCart stays in sync for the next syncCart-less lookup path.
      const cartObjEl = this.querySelector('#cart-obj');
      if (cartObjEl) {
        try {
          this.fullCart = JSON.parse(cartObjEl.textContent);
        } catch (_) { /* ignore parse errors */ }
      }
    }
  }

  refresh(data) {
    this.cart = data;
    this.renderCartItems();
  }
}

customElements.define('custom-cart', Cart);
